/**
 * routes.ts — where the application thinks it is (P2/US-212).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THE HASH, NOT THE PATH
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * `#/projets`, not `/projets`. The application is a static bundle served by `vite preview` today
 * and by Cloudflare Pages later; a real path would 404 on reload unless every host is configured to
 * rewrite unknown paths to `index.html`. A hash needs no server configuration anywhere, and this
 * tool has three views, not thirty.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THE DESTINATION IS PRESERVED ACROSS SIGN-IN
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * Sessions expire, and they expire while somebody is working. An engineer who follows a link to
 * `#/cas/<id>` with a stale cookie must land on that case after signing in, not on a blank
 * calculator — which is what US-212 means by "with the destination preserved, rather than to a
 * blank calculator that fails on its first request".
 *
 * The destination is kept in memory rather than in `sessionStorage`. It has to survive a sign-in,
 * which is a few seconds in the same page; it does **not** have to survive a reload, and a stored
 * destination that outlives its usefulness is how a user ends up bounced to a case they opened
 * last week every time they sign in.
 */

/** Every view this application has. */
export type Route =
  /** The calculator. The default, and where a signed-in user starts. */
  | { readonly name: 'calc' }
  /** The project and case browser (US-213). */
  | { readonly name: 'projets' }
  /** One stored case, opened read-first from its snapshot (US-214). */
  | { readonly name: 'cas'; readonly caseId: string }
  /** Invite acceptance. The one route reachable **without** a session. */
  | { readonly name: 'invitation'; readonly token: string };

export const DEFAULT_ROUTE: Route = { name: 'calc' };

/**
 * Read a route out of a `location.hash`.
 *
 * Anything unrecognised is the calculator. A 404 view for a mistyped fragment would be three more
 * screens of code to tell a user something the calculator itself makes obvious.
 */
export function parseHash(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '');
  const [head, tail] = raw.split('/');

  if (head === 'projets') return { name: 'projets' };
  if (head === 'cas' && tail !== undefined && tail !== '') {
    return { name: 'cas', caseId: decodeURIComponent(tail) };
  }
  if (head === 'invitation' && tail !== undefined && tail !== '') {
    return { name: 'invitation', token: decodeURIComponent(tail) };
  }

  return DEFAULT_ROUTE;
}

/** The hash a route is written as. The inverse of {@link parseHash} for every reachable route. */
export function toHash(route: Route): string {
  switch (route.name) {
    case 'calc':
      return '#/';
    case 'projets':
      return '#/projets';
    case 'cas':
      return `#/cas/${encodeURIComponent(route.caseId)}`;
    case 'invitation':
      return `#/invitation/${encodeURIComponent(route.token)}`;
  }
}

/**
 * Does this route need a session?
 *
 * Exactly one does not, and it mirrors the API's own public list: accepting an invite is how an
 * account comes into existence, so requiring a session for it would be a closed loop. Keeping the
 * two lists aligned matters — a route the browser thinks is public but the API does not would show
 * a form that 401s on submit.
 */
export function needsSession(route: Route): boolean {
  return route.name !== 'invitation';
}

/**
 * The router.
 *
 * A controller rather than a module-level singleton, for the same reason `createApp()` is a factory
 * on the API side: a test needs its own, and one shared listener across tests is how a suite starts
 * depending on its own execution order.
 */
export function router(options: {
  /** Called with the route to display. Never called with a route that needs a session while none. */
  readonly onRoute: (route: Route) => void;
  readonly location?: { hash: string };
  readonly listen?: (handler: () => void) => void;
}) {
  const target = options.location ?? window.location;
  let pendingDestination: Route | undefined;

  function current(): Route {
    return parseHash(target.hash);
  }

  const listen = options.listen ?? ((handler) => window.addEventListener('hashchange', handler));
  listen(() => options.onRoute(current()));

  return {
    current,

    /** Go somewhere. Setting the hash fires `hashchange`, which is what actually renders. */
    navigate(route: Route): void {
      const next = toHash(route);
      if (target.hash === next) {
        // Assigning the same hash fires no event, and a navigation that renders nothing looks like
        // a broken button. Render it directly instead.
        options.onRoute(route);
        return;
      }
      target.hash = next;
    },

    /**
     * Remember where the user was trying to go, before sending them to sign in.
     *
     * The invite route is never remembered: its token is single-use, so returning to it after a
     * successful sign-in would land on "cette invitation n'est plus valable" — a confusing end to a
     * sequence that actually worked.
     */
    rememberDestination(route: Route = current()): void {
      if (route.name === 'invitation') return;
      pendingDestination = route;
    },

    /** Take it back, once. Cleared on read so a later sign-in does not replay an old destination. */
    takeDestination(): Route {
      const destination = pendingDestination ?? DEFAULT_ROUTE;
      pendingDestination = undefined;
      return destination;
    },
  };
}
